import * as React from 'react';

import { useToast } from '@rematter/ui-web/hooks';
import { useAtom } from 'jotai';

import { useCoreUserDisableMutation } from '../_graphql/useCoreUserDisableMutation';
import { useCoreUserEnableMutation } from '../_graphql/useCoreUserEnableMutation';
import { useCoreUsersDeleteMutation } from '../_graphql/useCoreUsersDeleteMutation';
import { useCoreUsersDisableBulkMutation } from '../_graphql/useCoreUsersDisableBulkMutation';
import { useCoreUsersEnableBulkMutation } from '../_graphql/useCoreUsersEnableBulkMutation';
import type { UserRecord } from '../types';

import { $selectedUserIds } from '~/modules/settings/_state/teamPermissions.state';

export type PendingUserAction = {
  type: 'remove' | 'restore' | 'delete';
  user: UserRecord;
};

export type PendingBulkAction = 'remove' | 'restore' | 'delete';

export const useBulkUserActions = (allUsers: UserRecord[]) => {
  const toast = useToast();

  const [selectedUserIds, setSelectedUserIds] = useAtom($selectedUserIds);

  const disableMutation = useCoreUserDisableMutation();
  const enableMutation = useCoreUserEnableMutation();
  const deleteMutation = useCoreUsersDeleteMutation();
  const disableBulkMutation = useCoreUsersDisableBulkMutation();
  const enableBulkMutation = useCoreUsersEnableBulkMutation();

  const [actingOnUserId, setActingOnUserId] = React.useState<string | null>(
    null,
  );
  const [isBulkActionRunning, setIsBulkActionRunning] = React.useState(false);
  const [pendingAction, setPendingAction] =
    React.useState<PendingUserAction | null>(null);
  const [pendingBulkAction, setPendingBulkAction] =
    React.useState<PendingBulkAction | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = React.useState('');

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedUserIds),
    [selectedUserIds],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedUserIds(Array.from(keys) as string[]);
    },
    [setSelectedUserIds],
  );

  const { activeUserIds, archivedUserIds } = React.useMemo(() => {
    const active: string[] = [];
    const archived: string[] = [];
    for (const id of selectedUserIds) {
      const user = allUsers.find((u) => u.id === id);
      if (user?.status === 'archived') {
        archived.push(id);
      } else if (user) {
        active.push(id);
      }
    }
    return { activeUserIds: active, archivedUserIds: archived };
  }, [selectedUserIds, allUsers]);

  const handleRemoveAccess = (user: UserRecord) => {
    setPendingAction({ type: 'remove', user });
  };

  const handleRestoreAccess = (user: UserRecord) => {
    setPendingAction({ type: 'restore', user });
  };

  const handleDelete = (user: UserRecord) => {
    setDeleteConfirmName('');
    setPendingAction({ type: 'delete', user });
  };

  const closePendingAction = () => {
    if (
      !disableMutation.isPending &&
      !enableMutation.isPending &&
      !deleteMutation.isPending
    ) {
      setPendingAction(null);
      setDeleteConfirmName('');
    }
  };

  const handleConfirmRemoveAccess = async () => {
    if (!pendingAction || pendingAction.type !== 'remove') return;
    setActingOnUserId(pendingAction.user.id);
    try {
      await disableMutation.mutateAsync({ id: pendingAction.user.id });
      toast.success('Access removed');
      setPendingAction(null);
    } catch (err) {
      console.error('Failed to remove access:', err);
      toast.error('Failed to remove access');
    } finally {
      setActingOnUserId(null);
    }
  };

  const handleConfirmRestoreAccess = async () => {
    if (!pendingAction || pendingAction.type !== 'restore') return;
    setActingOnUserId(pendingAction.user.id);
    try {
      await enableMutation.mutateAsync({ id: pendingAction.user.id });
      toast.success('Access restored');
      setPendingAction(null);
    } catch (err) {
      console.error('Failed to restore access:', err);
      toast.error('Failed to restore access');
    } finally {
      setActingOnUserId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingAction || pendingAction.type !== 'delete') return;
    if (deleteConfirmName.trim() !== pendingAction.user.name) return;
    setActingOnUserId(pendingAction.user.id);
    try {
      await deleteMutation.mutateAsync({ userIds: [pendingAction.user.id] });
      toast.success('User deleted');
      setPendingAction(null);
      setDeleteConfirmName('');
    } catch (err) {
      console.error('Failed to delete user:', err);
      toast.error('Failed to delete user');
    } finally {
      setActingOnUserId(null);
    }
  };

  const handleBulkRemoveAccess = () => {
    if (activeUserIds.length === 0) return;
    setPendingBulkAction('remove');
  };

  const handleBulkRestoreAccess = () => {
    if (archivedUserIds.length === 0) return;
    setPendingBulkAction('restore');
  };

  const handleBulkDelete = () => {
    if (selectedUserIds.length === 0) return;
    setPendingBulkAction('delete');
  };

  const closePendingBulkAction = () => {
    if (
      !disableBulkMutation.isPending &&
      !enableBulkMutation.isPending &&
      !deleteMutation.isPending
    ) {
      setPendingBulkAction(null);
    }
  };

  const handleConfirmBulkRemoveAccess = async () => {
    setIsBulkActionRunning(true);
    try {
      await disableBulkMutation.mutateAsync({ userIds: activeUserIds });
      toast.success('Access removed for selected users');
      setSelectedUserIds([]);
      setPendingBulkAction(null);
    } catch (err) {
      console.error('Failed to remove access:', err);
      toast.error('Failed to remove access');
    } finally {
      setIsBulkActionRunning(false);
    }
  };

  const handleConfirmBulkRestoreAccess = async () => {
    setIsBulkActionRunning(true);
    try {
      await enableBulkMutation.mutateAsync({ userIds: archivedUserIds });
      toast.success('Access restored for selected users');
      setSelectedUserIds([]);
      setPendingBulkAction(null);
    } catch (err) {
      console.error('Failed to restore access:', err);
      toast.error('Failed to restore access');
    } finally {
      setIsBulkActionRunning(false);
    }
  };

  const handleConfirmBulkDelete = async () => {
    setIsBulkActionRunning(true);
    try {
      await deleteMutation.mutateAsync({ userIds: selectedUserIds });
      toast.success('Selected users deleted');
      setSelectedUserIds([]);
      setPendingBulkAction(null);
    } catch (err) {
      console.error('Failed to delete users:', err);
      toast.error('Failed to delete users');
    } finally {
      setIsBulkActionRunning(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedUserIds([]);
  };

  return {
    selectedUserIds,
    selectedKeys,
    handleSelectionChange,
    handleClearSelection,
    activeUserIds,
    archivedUserIds,
    actingOnUserId,
    isBulkActionRunning,
    pendingAction,
    pendingBulkAction,
    deleteConfirmName,
    setDeleteConfirmName,
    handleRemoveAccess,
    handleRestoreAccess,
    handleDelete,
    closePendingAction,
    handleConfirmRemoveAccess,
    handleConfirmRestoreAccess,
    handleConfirmDelete,
    handleBulkRemoveAccess,
    handleBulkRestoreAccess,
    handleBulkDelete,
    closePendingBulkAction,
    handleConfirmBulkRemoveAccess,
    handleConfirmBulkRestoreAccess,
    handleConfirmBulkDelete,
    isRemoveAccessPending: disableMutation.isPending,
    isRestoreAccessPending: enableMutation.isPending,
    isDeletePending: deleteMutation.isPending,
    isBulkRemoveAccessPending: disableBulkMutation.isPending,
    isBulkRestoreAccessPending: enableBulkMutation.isPending,
  };
};
